import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Pencil,
  Sparkles,
  Loader2,
  AlertTriangle,
} from "lucide-react";
import { Modal, ModalFooterButtons } from "../components/Modal";
import { Dropdown } from "../components/Dropdown";
import { DataTable, type Column } from "../components/DataTable";
import { RBACAction } from "../components/RBACButton";
import { Tip } from "../components/ui/tooltip";
import { showToast } from "../components/ui/custom-toast";
import { getAPI, putAPI } from "../../api/frameworksApi";
import {
  AI_INTERACTION_CONTROLLER_URL,
  AI_MODULE_ORDER,
  AI_PROVIDER_ACCENT_COLORS,
  AI_PROVIDER_ACCENT_DEFAULT,
  API_URL,
  EMPTY_PLACEHOLDER,
  LLM_ORG_STORAGE_KEY,
} from "../../constants";
import { formatDateTime } from "../../utils/formatters";

// ─── Types ────────────────────────────────────────────────────────────────────

interface OrgUnit {
  id: string;
  name: string;
  shortName?: string;
  orgType?: string;
  isActive?: boolean;
}

interface FeatureMapping {
  aiModule: string; // enum value — kept for the save payload (e.g. "DOCUMENT_VALIDATION")
  aiModuleLabel: string; // friendly display name (e.g. "Document Validation")
  provider: string; // enum value — kept for the save payload (e.g. "GROQ")
  providerLabel: string; // friendly display name (e.g. "Groq")
  modelName?: string;
  assigned?: boolean;
  updatedByName?: string; // Last Changed By
  updatedAt?: string; // Last Changed On
}

interface ProviderOption {
  value: string; // configId of the provider-model config — submitted in the save payload
  label: string; // provider (+ model) label — shown in the dropdown
  provider: string; // provider enum — only used to preselect the row's current assignment
  modelName?: string; // model name — only used to preselect the row's current assignment
}

// Ranks a module enum by its position in AI_MODULE_ORDER; unknown modules sort last.
function moduleRank(key: string): number {
  const i = AI_MODULE_ORDER.indexOf(key as (typeof AI_MODULE_ORDER)[number]);
  return i === -1 ? AI_MODULE_ORDER.length : i;
}

// Cosmetic accent color per provider enum (labels always come from the API).
function providerClass(provider: string): string {
  return AI_PROVIDER_ACCENT_COLORS[provider] ?? AI_PROVIDER_ACCENT_DEFAULT;
}

// Normalize a backend row into our shape (tolerates omitted / null fields).
function normalizeFeature(raw: Record<string, unknown>): FeatureMapping {
  const r = raw ?? {};
  return {
    aiModule: String(r.aiModule ?? ""),
    aiModuleLabel: String(r.aiModuleLabel ?? r.aiModule ?? ""),
    provider: r.provider ? String(r.provider) : "",
    providerLabel: r.providerLabel ? String(r.providerLabel) : "",
    modelName: (r.modelName as string | undefined) ?? undefined,
    assigned: (r.assigned as boolean | undefined) ?? undefined,
    updatedByName: (r.updatedByName as string | undefined) ?? undefined,
    updatedAt: (r.updatedAt as string | undefined) ?? undefined,
  };
}

// Normalize an assignable-provider entry into a dropdown option. The option's
// value is the config's `configId` (unique per provider-model) so the selected
// option submits its own configId — never a value derived from the table row.
function normalizeProviderOption(raw: Record<string, unknown>): ProviderOption {
  const r = raw ?? {};
  const value = String(r.configId ?? r.value ?? "");
  const provider = String(r.provider ?? "");
  const providerLabel = String(
    r.providerLabel ?? r.label ?? r.displayName ?? r.provider ?? "",
  );
  const modelName = (r.modelName as string | undefined) ?? undefined;
  const label = modelName ? `${providerLabel} - ${modelName}` : providerLabel;
  return { value, label, provider, modelName };
}

// Turn an axios-style error into a user-facing message: offline first, then the
// backend's structured `errors[0].detail`, then the caller's fallback.
function readApiError(err: unknown, fallback: string): string {
  const e = err as {
    message?: string;
    response?: { data?: { errors?: { detail?: string }[] } };
  };
  if (e?.message === "OFFLINE") {
    return "You appear to be offline. Check your connection and try again.";
  }
  return e?.response?.data?.errors?.[0]?.detail ?? fallback;
}

// ─── Page ──────────────────────────────────────────────────────────────────────

export function AiInteractionController() {
  const [features, setFeatures] = useState<FeatureMapping[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Organization context (shared with the LLM Configuration screen).
  const [orgs, setOrgs] = useState<OrgUnit[]>([]);
  const [orgsLoading, setOrgsLoading] = useState(true);
  const [orgsError, setOrgsError] = useState<string | null>(null);
  const [selectedOrgId, setSelectedOrgId] = useState<string>(
    () => localStorage.getItem(LLM_ORG_STORAGE_KEY) ?? "",
  );

  // Providers that can be assigned for the selected org.
  const [providerOptions, setProviderOptions] = useState<ProviderOption[]>([]);

  // Single-module (per-row) edit.
  const [editRow, setEditRow] = useState<FeatureMapping | null>(null);
  // Holds the selected dropdown option's `configId` — this is what gets submitted.
  const [rowConfigId, setRowConfigId] = useState<string>("");
  const [rowSaving, setRowSaving] = useState(false);

  // ── Fetch accessible orgs once ────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setOrgsLoading(true);
      setOrgsError(null);
      try {
        // Scope to the organizations the current user can access for LLM
        // configuration (module=llm_configuration, action=view) — the same RBAC
        // pattern the Roles → Group Mappings screen uses — instead of loading
        // every org unit via /org-units?flat=true. This hides unauthorized orgs
        // and keeps every downstream request scoped to a permitted org.
        const res = await getAPI(
          `${API_URL.ACCESSIBLE_ORGS}?module=llm_configuration&action=view&org_type=ORGANIZATION`,
        );
        if (cancelled) return;
        const list: OrgUnit[] = res?.data?.organizations ?? [];
        setOrgs(list);
        const stored = localStorage.getItem(LLM_ORG_STORAGE_KEY);
        const initial =
          stored && list.some((o) => o.id === stored)
            ? stored
            : (list[0]?.id ?? "");
        setSelectedOrgId(initial);
      } catch (e) {
        if (cancelled) return;
        setOrgsError(readApiError(e, "Failed to load organizations."));
      } finally {
        if (!cancelled) setOrgsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Persist org selection so it survives navigation and stays in sync.
  useEffect(() => {
    if (selectedOrgId) localStorage.setItem(LLM_ORG_STORAGE_KEY, selectedOrgId);
  }, [selectedOrgId]);

  // ── Fetch feature mappings for the selected org ───────────────────────────
  const loadFeatures = useCallback(async () => {
    if (!selectedOrgId) {
      setFeatures([]);
      setLoading(false);
      return;
    }
    // Keep old rows visible while refreshing — never blank the table.
    setError(null);
    try {
      const res = await getAPI(AI_INTERACTION_CONTROLLER_URL.forOrg(selectedOrgId));
      const raw: Record<string, unknown>[] = res?.data ?? [];
      const normalized = raw
        .map(normalizeFeature)
        .sort((a, b) => moduleRank(a.aiModule) - moduleRank(b.aiModule));
      setFeatures(normalized);
    } catch (err) {
      setError(readApiError(err, "Could not load AI feature mappings."));
    } finally {
      setLoading(false);
    }
  }, [selectedOrgId]);

  // ── Fetch assignable providers for the selected org ───────────────────────
  const loadProviders = useCallback(async () => {
    if (!selectedOrgId) {
      setProviderOptions([]);
      return;
    }
    try {
      const res = await getAPI(
        AI_INTERACTION_CONTROLLER_URL.assignableProviders(selectedOrgId),
      );
      const raw: Record<string, unknown>[] = res?.data ?? [];
      setProviderOptions(
        raw.map(normalizeProviderOption).filter((o) => o.value),
      );
    } catch {
      // Non-fatal — the Edit dropdown simply shows no options.
      setProviderOptions([]);
    }
  }, [selectedOrgId]);

  useEffect(() => {
    setLoading(true);
    void loadFeatures();
    void loadProviders();
  }, [loadFeatures, loadProviders]);

  const selectedOrg = useMemo(
    () => orgs.find((o) => o.id === selectedOrgId),
    [orgs, selectedOrgId],
  );

  // ── Per-row edit: open modal for a single module ──
  const openRowEdit = (row: FeatureMapping) => {
    setEditRow(row);
    // Preselect the option matching the row's current provider-model (display
    // convenience only). The submitted configId always comes from whichever
    // option is selected here — never from a field on the row itself.
    const current = providerOptions.find(
      (o) =>
        o.provider === row.provider &&
        (o.modelName ?? "") === (row.modelName ?? ""),
    );
    setRowConfigId(current?.value ?? "");
    setRowSaving(false);
  };

  const handleRowSave = async () => {
    if (!editRow) return;
    // Guard: never submit a null/empty configId, which would clear the module.
    if (!rowConfigId) {
      showToast("error", "Select a provider model before saving.");
      return;
    }
    setRowSaving(true);
    try {
      // Single-module update — touches only the edited module, so the other
      // modules' assignments can never be accidentally cleared. configId comes
      // straight from the selected dropdown option (the assignable-providers
      // response), never derived from the table row.
      await putAPI(AI_INTERACTION_CONTROLLER_URL.single, {
        orgId: selectedOrgId,
        aiModule: editRow.aiModule,
        configId: rowConfigId,
      });
      showToast("success", "AI feature provider updated successfully.");
      setEditRow(null);
      await loadFeatures();
    } catch (err) {
      showToast(
        "error",
        readApiError(err, "Could not update AI feature provider."),
      );
    } finally {
      setRowSaving(false);
    }
  };

  // ── Main table columns ──
  const columns: Column<FeatureMapping>[] = [
    {
      key: "no",
      header: "#",
      align: "center",
      render: (_row, index) => <span>{index + 1}</span>,
    },
    {
      key: "aiModule",
      header: "AI Features",
      align: "left",
      render: (row) => (
        <span>{row.aiModuleLabel || row.aiModule || EMPTY_PLACEHOLDER}</span>
      ),
    },
    {
      key: "provider",
      header: "Provider",
      align: "left",
      render: (row) =>
        row.provider ? (
          <span className={`font-medium ${providerClass(row.provider)}`}>
            {row.providerLabel || row.provider}
          </span>
        ) : (
          <span className="text-[#94a3b8] dark:text-white/40">
            {EMPTY_PLACEHOLDER}
          </span>
        ),
    },
    {
      key: "modelName",
      header: "Provider Model",
      align: "left",
      render: (row) => (
        <span>{row.modelName || EMPTY_PLACEHOLDER}</span>
      ),
    },
    {
      key: "updatedByName",
      header: "Last Changed By",
      align: "left",
      render: (row) => <span>{row.updatedByName || EMPTY_PLACEHOLDER}</span>,
    },
    {
      key: "updatedAt",
      header: "Last Changed On",
      align: "left",
      render: (row) => <span>{formatDateTime(row.updatedAt)}</span>,
    },
    {
      key: "action",
      header: "Action",
      align: "center",
      render: (row) => (
        <RBACAction module="llm_configuration" action="update">
          <Tip label="Edit AI Feature" side="bottom">
            <button
              type="button"
              onClick={() => openRowEdit(row)}
              disabled={loading || !selectedOrgId}
              aria-label="Edit provider"
              className="inline-flex items-center justify-center p-1.5 rounded-md text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 dark:text-indigo-400 dark:hover:text-indigo-300 dark:hover:bg-indigo-500/10 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Pencil className="size-4" />
            </button>
          </Tip>
        </RBACAction>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">
            AI Module Configuration
          </h1>
          <p className="text-sm d mt-0.5">
            Provider mapping for each AI feature. Use Edit to change which LLM
            provider powers a feature. The organization is selected on the LLM
            Configuration screen and shared across the module.
          </p>
        </div>
      </div>

      {/* Org-level error */}
      {orgsError && (
        <div className="flex items-center gap-3 px-4 py-3 rounded-lg bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 text-red-700 dark:text-red-400">
          <AlertTriangle className="size-4 flex-shrink-0" />
          <p className="text-sm flex-1">{orgsError}</p>
        </div>
      )}

      {/* Mapping table — larger rows and text for readability. The scoped
          selectors bump the shared DataTable's font size for this page only. */}
      <div className="[&_table]:text-[15px] [&_th]:text-[15px]">
        <DataTable
          columns={columns}
          data={features}
          loading={loading}
          error={error}
          onRetry={loadFeatures}
          rowKey={(r) => r.aiModule}
          emptyText={
            !selectedOrgId
              ? "Select an organization to view its AI feature mappings."
              : "No AI features configured."
          }
          emptyIcon={<Sparkles className="size-4 text-[#64748b] dark:text-white/40" />}
        />
      </div>

      {/* Single-module edit modal (per-row Action) */}
      <Modal
        open={editRow !== null}
        onClose={() => !rowSaving && setEditRow(null)}
        title="Update AI Module Configuration"
        description={selectedOrg ? `Organization: ${selectedOrg.name}` : undefined}
        closable={!rowSaving}
        footer={
          <ModalFooterButtons
            onCancel={() => setEditRow(null)}
            onSubmit={handleRowSave}
            submitLabel={rowSaving ? "Saving…" : "Save"}
            submitDisabled={rowSaving}
          />
        }
      >
        {editRow && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:items-start">
              <div>
                <span className="block text-xs font-medium d mb-1.5">
                  AI Feature
                </span>
                <p className="text-sm font-semibold text-foreground">
                  {editRow.aiModuleLabel ||
                    editRow.aiModule ||
                    EMPTY_PLACEHOLDER}
                </p>
              </div>
              <div>
                <span className="block text-xs font-medium d mb-1.5">
                  Provider Model
                </span>
                <Dropdown
                  value={rowConfigId}
                  onChange={setRowConfigId}
                  options={providerOptions}
                  placeholder="Select provider model"
                  variant="form"
                />
              </div>
            </div>
            {rowSaving && (
              <div className="flex items-center gap-2 text-sm d">
                <Loader2 className="size-4 animate-spin" />
                Saving changes…
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}
